import {
  IconFinance,
  IconOrders,
  IconCustomer,
  IconDeliveryTruck,
  WarningIcon,
} from '../../assets/svg/icons';

export interface ReportColumnDef {
  key: string;
  labelId: string;
  defaultLabel: string;
}

export interface ReportTypeDef {
  type: string;
  titleId: string;
  defaultTitle: string;
  descId: string;
  defaultDesc: string;
  icon: React.ComponentType;
  accent: 'green' | 'blue' | 'red' | 'purple' | 'teal';
  columns: ReportColumnDef[];
}

// Column keys mirror the OMA ReportDefinitions exactly (snake_case-stable so
// they survive the BFF passthrough). The order-level mart has no line-items or
// sales-channel field, so the orders report shows Reference/Region in place of
// the design's "Items"/"Channel".
const COLUMN_LABEL: Record<string, string> = {
  order_id: 'Order ID',
  date: 'Date',
  customer: 'Customer',
  reference: 'Reference',
  total: 'Total',
  region: 'Region',
  status: 'Status',
  gross_amount: 'Gross amount',
  fees: 'Fees',
  net_payout: 'Net payout',
  payment_method: 'Payment method',
  amount: 'Amount',
  failure_reason: 'Failure reason',
  retry_count: 'Retry count',
  first_order: 'First order',
  last_order: 'Last order',
  orders_count: 'Orders count',
  total_spent: 'Total spent',
  avg_order_value: 'Avg order value',
  city: 'City',
  product_sku: 'SKU',
  product_name: 'Product',
  quantity: 'Quantity',
  province: 'Province',
  created: 'Created',
  pickup_date: 'Pickup date',
  delivery_date: 'Delivery date',
  original_delivery_date: 'Original delivery',
  attempts: 'Attempts',
  return_date: 'Return date',
  return_reason: 'Return reason',
  last_action: 'Last action',
  return_reason_code: 'Return reason code',
  reason_code_name: 'Reason code name',
  first_ofd: 'First OFD',
  last_ofd: 'Last OFD',
};

const cols = (...keys: string[]): ReportColumnDef[] =>
  keys.map((key) => ({
    key,
    labelId: `reports.column.${key}`,
    defaultLabel: COLUMN_LABEL[key] ?? key,
  }));

export const REPORT_TYPES: ReportTypeDef[] = [
  {
    type: 'financial',
    titleId: 'reports.financial.title',
    defaultTitle: 'Financial report',
    descId: 'reports.financial.desc',
    defaultDesc: 'Gross sales, fees and net payouts per order.',
    icon: IconFinance,
    accent: 'green',
    columns: cols('date', 'order_id', 'gross_amount', 'fees', 'net_payout', 'payment_method', 'status'),
  },
  {
    type: 'orders',
    titleId: 'reports.orders.title',
    defaultTitle: 'Orders report',
    descId: 'reports.orders.desc',
    defaultDesc: 'Every order with items, totals and channel.',
    icon: IconOrders,
    accent: 'blue',
    columns: cols('order_id', 'date', 'customer', 'reference', 'total', 'region', 'status'),
  },
  {
    type: 'failed-orders',
    titleId: 'reports.failed.title',
    defaultTitle: 'Failed orders report',
    descId: 'reports.failed.desc',
    defaultDesc: 'Declined and errored orders with failure reasons.',
    icon: WarningIcon,
    accent: 'red',
    columns: cols('order_id', 'date', 'customer', 'amount', 'failure_reason', 'payment_method', 'retry_count'),
  },
  {
    type: 'customer-activity',
    titleId: 'reports.customer.title',
    defaultTitle: 'Customer activity report',
    descId: 'reports.customer.desc',
    defaultDesc: 'Order frequency, spend and recency per customer.',
    icon: IconCustomer,
    accent: 'purple',
    columns: cols('customer', 'first_order', 'last_order', 'orders_count', 'total_spent', 'avg_order_value', 'city'),
  },
  {
    type: 'fulfillment',
    titleId: 'reports.fulfillment.title',
    defaultTitle: 'Fulfillment report',
    descId: 'reports.fulfillment.desc',
    defaultDesc: 'Per-product fulfillment: pickup, delivery, attempts and returns.',
    icon: IconDeliveryTruck,
    accent: 'teal',
    columns: cols(
      'order_id', 'reference', 'status', 'province', 'created', 'last_action', 'attempts',
      'pickup_date', 'delivery_date', 'original_delivery_date', 'return_date', 'return_reason_code',
      'reason_code_name', 'return_reason', 'first_ofd', 'last_ofd', 'product_sku', 'product_name', 'quantity'
    ),
  },
];

export const DEFAULT_RANGE = 'last-30-days';

// Ordered for the date-range pills in the configure panel.
export const RANGE_PRESETS = ['today', 'last-7-days', 'last-30-days', 'last-90-days', 'custom'];

export const RANGE_PRESET_LABELS: Record<string, { id: string; defaultMessage: string }> = {
  today: { id: 'reports.range.today', defaultMessage: 'Today' },
  'last-7-days': { id: 'reports.range.7', defaultMessage: 'Last 7 days' },
  'last-30-days': { id: 'reports.range.30', defaultMessage: 'Last 30 days' },
  'last-90-days': { id: 'reports.range.90', defaultMessage: 'Last 90 days' },
  custom: { id: 'reports.range.custom', defaultMessage: 'Custom' },
};

export const reportTypeById = (type: string): ReportTypeDef | undefined =>
  REPORT_TYPES.find((r) => r.type === type);
